import { Pause, Play, RotateCcw, Save, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './countdown-panel.css';

type CountdownStatus = 'stopped' | 'running' | 'paused' | 'finished';
type CountdownAction = 'configure' | 'start' | 'pause' | 'resume' | 'restart' | 'stop';

type CountdownSnapshot = {
  version: 1;
  enabled: true;
  status: CountdownStatus;
  durationSeconds: number;
  remainingSeconds: number;
  endAt: string | null;
  updatedAt: string;
  outputDirectory: string;
  outputFilename: string;
};

async function countdownRequest(
  action?: CountdownAction,
  durationSeconds?: number,
): Promise<CountdownSnapshot> {
  const init: RequestInit = {
    method: action ? 'POST' : 'GET',
    credentials: 'same-origin',
  };
  if (action) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify({
      action,
      ...(durationSeconds === undefined ? {} : { durationSeconds }),
    });
  }

  const response = await fetch('/api/v1/text-outputs/countdown', init);

  if (!response.ok) {
    let message = `Countdown request failed (${response.status}).`;
    try {
      const body = (await response.json()) as { message?: unknown; error?: unknown };
      if (typeof body.message === 'string') message = body.message;
      else if (typeof body.error === 'string') message = body.error;
    } catch {
      // Retain the status-based message.
    }
    throw new Error(message);
  }

  return (await response.json()) as CountdownSnapshot;
}

function clampInteger(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, Math.min(max, parsed));
}

function splitDuration(seconds: number): { hours: string; minutes: string; seconds: string } {
  const safe = Math.max(1, Math.floor(seconds));
  return {
    hours: String(Math.floor(safe / 3600)),
    minutes: String(Math.floor((safe % 3600) / 60)),
    seconds: String(safe % 60),
  };
}

function remainingSeconds(snapshot: CountdownSnapshot, now: number): number {
  if (snapshot.status === 'finished') return 0;
  if (snapshot.status === 'stopped') return snapshot.durationSeconds;
  if (snapshot.status === 'paused') return snapshot.remainingSeconds;
  if (!snapshot.endAt) return snapshot.remainingSeconds;
  const endAt = Date.parse(snapshot.endAt);
  if (!Number.isFinite(endAt)) return snapshot.remainingSeconds;
  return Math.max(0, Math.ceil((endAt - now) / 1000));
}

function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(remainder)}`
    : `${pad(minutes)}:${pad(remainder)}`;
}

export function CountdownPanel() {
  const [snapshot, setSnapshot] = useState<CountdownSnapshot | null>(null);
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('10');
  const [seconds, setSeconds] = useState('0');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [clock, setClock] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const next = await countdownRequest();
      setSnapshot(next);
      const duration = splitDuration(next.durationSeconds);
      setHours(duration.hours);
      setMinutes(duration.minutes);
      setSeconds(duration.seconds);
      setError('');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Countdown could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (snapshot?.status !== 'running') return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load, snapshot?.status]);

  const draftDuration = useMemo(() => {
    const h = clampInteger(hours, 0, 99);
    const m = clampInteger(minutes, 0, 59);
    const s = clampInteger(seconds, 0, 59);
    return Math.max(1, Math.min(99 * 3600 + 59 * 60 + 59, h * 3600 + m * 60 + s));
  }, [hours, minutes, seconds]);

  const shownRemaining = snapshot ? remainingSeconds(snapshot, clock) : 0;
  const status = snapshot?.status ?? 'stopped';
  const running = status === 'running';
  const paused = status === 'paused';
  const outputPath = snapshot
    ? `${snapshot.outputDirectory.replace(/[\\/]+$/, '')}\\${snapshot.outputFilename}`
    : 'C:\\ProgramData\\MutedFFS\\TextOutputs\\countdown.txt';

  async function run(action: CountdownAction, includeDuration = false, success = '') {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const next = await countdownRequest(action, includeDuration ? draftDuration : undefined);
      setSnapshot(next);
      const duration = splitDuration(next.durationSeconds);
      setHours(duration.hours);
      setMinutes(duration.minutes);
      setSeconds(duration.seconds);
      setMessage(success);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Countdown action failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ffs-workspace-card ffs-countdown-card">
      <div className="ffs-countdown-heading">
        <div>
          <small>OBS COUNTDOWN</small>
          <strong>{formatCountdown(shownRemaining)}</strong>
        </div>
        <span className={`ffs-countdown-status ${status}`}>{status}</span>
      </div>

      <div className="ffs-countdown-duration" aria-label="Countdown duration">
        <label><span>Hours</span><input type="number" min="0" max="99" value={hours} disabled={busy || running} onChange={(event) => setHours(event.target.value)} /></label>
        <label><span>Minutes</span><input type="number" min="0" max="59" value={minutes} disabled={busy || running} onChange={(event) => setMinutes(event.target.value)} /></label>
        <label><span>Seconds</span><input type="number" min="0" max="59" value={seconds} disabled={busy || running} onChange={(event) => setSeconds(event.target.value)} /></label>
      </div>

      <div className="ffs-countdown-actions">
        <button type="button" className="ffs-button secondary" disabled={busy || running} onClick={() => void run('configure', true, 'Countdown duration set.')}><Save size={13} /> Set</button>
        <button type="button" className="ffs-button primary" disabled={busy || running} onClick={() => void run('start', true, 'Countdown started.')}><Play size={13} /> Start</button>
        {paused ? (
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void run('resume', false, 'Countdown resumed.')}><Play size={13} /> Resume</button>
        ) : (
          <button type="button" className="ffs-button secondary" disabled={busy || !running} onClick={() => void run('pause', false, 'Countdown paused.')}><Pause size={13} /> Pause</button>
        )}
        <button type="button" className="ffs-button secondary" disabled={busy || !snapshot} onClick={() => void run('restart', false, 'Countdown restarted.')}><RotateCcw size={13} /> Restart</button>
        <button type="button" className="ffs-button warning" disabled={busy || status === 'stopped'} onClick={() => void run('stop', false, 'Countdown stopped.')}><Square size={12} /> Stop</button>
      </div>

      <div className="ffs-countdown-path"><span>OBS file</span><code>{outputPath}</code></div>
      {message ? <div className="ffs-countdown-message">{message}</div> : null}
      {error ? <div className="ffs-countdown-error">{error}</div> : null}
    </section>
  );
}
